/**
 * OfflineQueueIndicator — Story X1.3.3 (PRD X1 §FR-X1-9).
 *
 * Chip global e informativo (não-clicável) que comunica o estado da fila de
 * ações offline. Montar este componente também ATIVA o driver `useOfflineQueue`
 * (detecção de rede + flush ao reconectar), por isso fica na shell autenticada.
 *
 * Visibilidade:
 *   - online & fila vazia      → nada (return null)
 *   - offline                  → "Offline · N pendente(s)" (ícone WifiOff)
 *   - online & fila não-vazia  → "Sincronizando… · N" (ícone RefreshCw girando)
 *
 * `pointer-events-none`: nunca intercepta cliques (mobile tem A2HS banner +
 * hambúrguer em posições fixas — o indicador é só leitura).
 */

import { RefreshCw, WifiOff } from "lucide-react";

import { cn } from "@/lib/utils";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";

export function OfflineQueueIndicator() {
  const { isOnline, pendingCount } = useOfflineQueue();

  const visible = !isOnline || pendingCount > 0;
  if (!visible) return null;

  const syncing = isOnline && pendingCount > 0;
  const countLabel =
    pendingCount > 0
      ? `${pendingCount} ${pendingCount === 1 ? "ação pendente" : "ações pendentes"}`
      : "";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-queue-indicator"
      data-state={syncing ? "syncing" : "offline"}
      className="pointer-events-none fixed bottom-4 left-1/2 z-40 -translate-x-1/2"
    >
      <div
        className={cn(
          "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-md",
          syncing
            ? "bg-primary/90 text-primary-foreground"
            : "bg-muted text-muted-foreground ring-1 ring-border"
        )}
      >
        {syncing ? (
          <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <WifiOff className="h-3.5 w-3.5" aria-hidden />
        )}
        <span>
          {syncing ? "Sincronizando…" : "Offline"}
          {countLabel ? ` · ${countLabel}` : ""}
        </span>
      </div>
    </div>
  );
}
